import React from "react";
import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Slider } from "../ui/slider";
import { Button } from "../ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";

const ARR_PRESETS = [
    { label: "5M", value: 5_000_000 },
    { label: "10M", value: 10_000_000 },
    { label: "20M", value: 20_000_000 },
    { label: "30M", value: 30_000_000 },
    { label: "50M", value: 50_000_000 },
];

const VERTICALS = [
    { name: "B2B SaaS (Horizontal)", nrr: 1.15 },
    { name: "Cybersecurity", nrr: 1.05 },
    { name: "HR Tech", nrr: 1.03 },
    { name: "MarTech", nrr: 1.07 },
    { name: "FinTech", nrr: 1.25 },
    { name: "Sales Tech", nrr: 1.1 },
];

const formatMoney = (n: number) => {
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
};

interface Point {
    month: number;
    value: number;
}

const buildSeries = (start: number, annualMultiplier: number): Point[] => {
    const points: Point[] = [];
    // monthly compounding equivalent
    const monthlyRate = Math.pow(annualMultiplier, 1 / 12);
    for (let m = 0; m <= 36; m++) {
        points.push({ month: m, value: start * Math.pow(monthlyRate, m) });
    }
    return points;
};

const NRRChart = ({
                      startValue,
                      benchmarkMultiplier,
                      trajectoryMultiplier,
                  }: {
    startValue: number;
    benchmarkMultiplier: number;
    trajectoryMultiplier: number;
}) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!svgRef.current || !containerRef.current) return;

        const benchmark = buildSeries(startValue, benchmarkMultiplier);
        const trajectory = buildSeries(startValue, trajectoryMultiplier);

        const containerWidth = containerRef.current.clientWidth;
        const width = containerWidth;
        const height = 460;
        const margin = { top: 24, right: 32, bottom: 48, left: 72 };
        const innerW = width - margin.left - margin.right;
        const innerH = height - margin.top - margin.bottom;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();
        svg.attr("viewBox", `0 0 ${width} ${height}`).attr("width", "100%").attr("height", height);

        const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

        const x = d3.scaleLinear().domain([0, 36]).range([0, innerW]);
        const maxY = d3.max([...benchmark, ...trajectory], (d) => d.value) ?? startValue;
        const minY = d3.min([...benchmark, ...trajectory], (d) => d.value) ?? 0;
        // Center the starting value vertically: pick the larger distance from start
        // to either extreme, then pad slightly so the chart fits with minor whitespace.
        const spread = Math.max(maxY - startValue, startValue - minY);
        const pad = spread * 0.15;
        const y = d3
            .scaleLinear()
            .domain([startValue - spread - pad, startValue + spread + pad])
            .range([innerH, 0]);

        // CSS variables -> resolve actual colors
        const styles = getComputedStyle(document.documentElement);
        const twColor = (v: string) => `${styles.getPropertyValue(v).trim()}`;
        const primary = twColor("--color-primary");
        const secondary = twColor("--color-secondary");
        const muted = twColor("--color-muted-foreground");
        const border = twColor("--color-border");

        // Quarterly gridlines (every 3 months)
        const quarters = d3.range(0, 37, 3);
        g.append("g")
            .attr("class", "grid")
            .selectAll("line")
            .data(quarters)
            .enter()
            .append("line")
            .attr("x1", (d) => x(d))
            .attr("x2", (d) => x(d))
            .attr("y1", 0)
            .attr("y2", innerH)
            .attr("stroke", border)
            .attr("stroke-width", (d) => (d % 12 === 0 ? 1.2 : 0.6))
            .attr("stroke-dasharray", (d) => (d % 12 === 0 ? "0" : "2,3"));

        // Y gridlines
        g.append("g")
            .selectAll("line")
            .data(y.ticks(5))
            .enter()
            .append("line")
            .attr("x1", 0)
            .attr("x2", innerW)
            .attr("y1", (d) => y(d))
            .attr("y2", (d) => y(d))
            .attr("stroke", border)
            .attr("stroke-opacity", 0.4);

        // X axis labels
        const xLabels: { m: number; label: string }[] = [
            { m: 0, label: "Now" },
            { m: 12, label: "M12" },
            { m: 24, label: "M24" },
            { m: 36, label: "M36" },
        ];
        g.append("g")
            .attr("transform", `translate(0,${innerH})`)
            .selectAll("text")
            .data(xLabels)
            .enter()
            .append("text")
            .attr("x", (d) => x(d.m))
            .attr("y", 24)
            .attr("text-anchor", "middle")
            .attr("fill", muted)
            .attr("font-size", 12)
            .text((d) => d.label);

        // X axis line
        g.append("line")
            .attr("x1", 0)
            .attr("x2", innerW)
            .attr("y1", innerH)
            .attr("y2", innerH)
            .attr("stroke", muted);

        // Y axis labels
        g.append("g")
            .selectAll("text")
            .data(y.ticks(5))
            .enter()
            .append("text")
            .attr("x", -12)
            .attr("y", (d) => y(d))
            .attr("dy", "0.32em")
            .attr("text-anchor", "end")
            .attr("fill", muted)
            .attr("font-size", 12)
            .text((d) => formatMoney(d));

        const line = d3
            .line<Point>()
            .x((d) => x(d.month))
            .y((d) => y(d.value))
            .curve(d3.curveMonotoneX);

        // Benchmark (dotted)
        g.append("path")
            .datum(benchmark)
            .attr("fill", "none")
            .attr("stroke", primary)
            .attr("stroke-width", 2.5)
            .attr("stroke-dasharray", "4,4")
            .attr("d", line);

        // Your trajectory (solid)
        g.append("path")
            .datum(trajectory)
            .attr("fill", "none")
            .attr("stroke", secondary)
            .attr("stroke-width", 3)
            .attr("d", line);

        // Legend
        const legend = g.append("g").attr("transform", `translate(${innerW - 180}, 8)`);
        legend
            .append("line")
            .attr("x1", 0).attr("x2", 24).attr("y1", 6).attr("y2", 6)
            .attr("stroke", primary).attr("stroke-width", 2.5).attr("stroke-dasharray", "4,4");
        legend.append("text").attr("x", 30).attr("y", 10).attr("fill", muted).attr("font-size", 12).text("Benchmark");
        legend
            .append("line")
            .attr("x1", 0).attr("x2", 24).attr("y1", 26).attr("y2", 26)
            .attr("stroke", secondary).attr("stroke-width", 3);
        legend.append("text").attr("x", 30).attr("y", 30).attr("fill", muted).attr("font-size", 12).text("Your trajectory");
    }, [startValue, benchmarkMultiplier, trajectoryMultiplier]);

    return (
        <div ref={containerRef} className="w-full">
            <svg ref={svgRef} />
        </div>
    );
};

const calculateValueAtMonth = (start: number, annualMultiplier: number, month: number): number => {
    const monthlyRate = Math.pow(annualMultiplier, 1 / 12);
    return start * Math.pow(monthlyRate, month);
};

const NRRCalculator = () => {
    const [startValue, setStartValue] = useState(20_000_000);
    const [benchmark, setBenchmark] = useState(1.06);
    const [trajectory, setTrajectory] = useState(0.95);

    // Calculate values for display cards
    const trajectoryM12 = calculateValueAtMonth(startValue, trajectory, 12);
    const benchmarkM12 = calculateValueAtMonth(startValue, benchmark, 12);
    const differenceM12 = trajectoryM12 - benchmarkM12;

    const trajectoryM36 = calculateValueAtMonth(startValue, trajectory, 36);
    const benchmarkM36 = calculateValueAtMonth(startValue, benchmark, 36);
    const differenceM36 = trajectoryM36 - benchmarkM36;

    return (

        <section className="section-padding bg-background">
            <div className="container mx-auto px-4 md:px-8 max-w-6xl">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
                    Net Revenue Retention Calculator
                </h1>
                <p className="text-muted-foreground text-lg mb-8 max-w-3xl">
                    Visualize how your NRR trajectory compares to industry benchmarks over a three-year horizon.
                </p>

                {/* Current ARR & Vertical selectors */}
                <div className="grid gap-6 md:grid-cols-2 mb-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Current ARR</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {ARR_PRESETS.map((p) => (
                                    <Button
                                        key={p.label}
                                        variant={startValue === p.value ? "default" : "outline"}
                                        onClick={() => setStartValue(p.value)}
                                    >
                                        {p.label}
                                    </Button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Your Vertical</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Select
                                value={String(benchmark)}
                                onValueChange={(v) => setBenchmark(Number(v))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select an industry" />
                                </SelectTrigger>
                                <SelectContent>
                                    {VERTICALS.map((v) => (
                                        <SelectItem key={v.name} value={String(v.nrr)}>
                                            {v.name} ({(v.nrr * 100).toFixed(0)}%)
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>
                </div>

                {/* Trajectory Slider - Full Width */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">Your Trajectory NRR</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <Slider
                                value={[trajectory]}
                                onValueChange={(value) => setTrajectory(value[0])}
                                min={0.7}
                                max={1.3}
                                step={0.01}
                                className="w-full"
                            />
                            <div className="flex justify-between text-sm text-muted-foreground">
                                <span>70%</span>
                                <span className="font-medium text-foreground">{(trajectory * 100).toFixed(0)}%</span>
                                <span>130%</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading">3-Year NRR Projection</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <NRRChart
                            startValue={startValue}
                            benchmarkMultiplier={benchmark}
                            trajectoryMultiplier={trajectory}
                        />
                    </CardContent>
                </Card>

                {/* Data Cards - Year 1 and Year 3 */}
                <div className="grid gap-6 md:grid-cols-2 mt-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Year 1 (M12)</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Your Trajectory</span>
                                <span className="font-medium text-foreground">{formatMoney(trajectoryM12)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Benchmark</span>
                                <span className="font-medium text-primary">{formatMoney(benchmarkM12)}</span>
                            </div>
                            <div className="border-t pt-3 mt-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">Difference</span>
                                    <span className={`font-semibold ${differenceM12 >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {differenceM12 >= 0 ? '+' : ''}{formatMoney(differenceM12)}
                </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Year 3 (M36)</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Your Trajectory</span>
                                <span className="font-medium text-foreground">{formatMoney(trajectoryM36)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Benchmark</span>
                                <span className="font-medium text-primary">{formatMoney(benchmarkM36)}</span>
                            </div>
                            <div className="border-t pt-3 mt-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground">Difference</span>
                                    <span className={`font-semibold ${differenceM36 >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {differenceM36 >= 0 ? '+' : ''}{formatMoney(differenceM36)}
                </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
};

export default NRRCalculator;
